import React, { useState, useEffect } from 'react';
import { fetchOrderMethod } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';
import { toTitleCase } from '../utils/format';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];
const PAGE_SIZE = 50;
const fmt = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OrderMethod() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);
  const [page, setPage] = useState(1);
  const [view, setView] = useState('default');

  const load = () => {
    setLoading(true);
    setError(null);
    fetchOrderMethod({ StartDate: toApiDate(start), EndDate: toApiDate(end), PageNumber: page, PageSize: PAGE_SIZE, view })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page, view]);

  const rows = Array.isArray(data) ? data : data?.data ?? data?.records?.data ?? [];
  const total = data?.totalRows ?? data?.total ?? rows.length;

  // If rows look like aggregated method stats
  const isMethodSummary = rows.length > 0 && (rows[0].orderMethod ?? rows[0].method ?? rows[0].paymentMethod) !== undefined && !rows[0].orderId;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Order Method Report</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Date Range</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <button onClick={() => { setPage(1); load(); }} className="btn btn-primary" style={{ fontSize: 11 }}>Run Report</button>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 11, marginLeft: 'auto' }}>
            <span style={{ color: 'var(--gray-500)', fontWeight: 600 }}>View:</span>
            <button className="btn" style={{ fontSize: 11, fontWeight: view === 'default' ? 700 : 400, color: view === 'default' ? 'var(--orange)' : 'var(--gray-500)' }} onClick={() => setView('default')}>Default (all)</button>
            <button className="btn" style={{ fontSize: 11, fontWeight: view === 'ap' ? 700 : 400, color: view === 'ap' ? 'var(--orange)' : 'var(--gray-500)' }} onClick={() => setView('ap')}>Admin Portal (AP)</button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Results {total > 0 && `(${total.toLocaleString()})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No data found.</div>
        )}
        {rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                    {isMethodSummary ? (
                      <>
                        <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order Method</th>
                        <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Orders</th>
                        <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Revenue</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Avg Order</th>
                      </>
                    ) : (
                      <>
                        <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                        <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                        <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Method</th>
                        <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Total</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                      {isMethodSummary ? (
                        <>
                          <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 600 }}>{row.orderMethod ?? row.method ?? row.paymentMethod ?? '—'}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--orange)', fontWeight: 700 }}>{Number(row.orderCount ?? row.count ?? 0).toLocaleString()}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right' }}>{fmt(row.totalRevenue ?? row.totalOrderAmount ?? row.revenue)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                            {row.orderCount ? fmt((row.totalRevenue ?? row.totalOrderAmount ?? row.revenue ?? 0) / row.orderCount) : '—'}
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId ?? row.orderId ?? '—'}</td>
                          <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{toTitleCase(row.billTo ?? row.customerName ?? '—')}</td>
                          <td style={{ padding: '8px 8px', color: 'var(--gray-700)' }}>{row.orderMethod ?? row.paymentMethod ?? '—'}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600 }}>{fmt(row.orderTotal ?? row.totalOrderAmount)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                            {row.orderDate ? new Date(row.orderDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>← Prev</button>
                <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
